import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ObjectiveProposalFlow } from "../../components/ProposalFlow/ObjectiveProposalFlow";
import { ProposalDisclaimer } from "../../components/ProposalFlow/ProposalDisclaimer";
import { ProposalProductInput } from "../../components/ProposalFlow/types";
import { useUser } from "../../hooks/useUser";

// Public, embeddable proposal wizard page — same shell pattern as
// src/pages/publicPdpAudit/PdpAuditPage.tsx: bare route (no portal chrome),
// ?bg= background override, `pdp-audit-embed` body class for iframe embeds.
// Product context arrives via query params (e.g. from the PDP audit report's
// "Create Proposal" CTA): ?title=&url=&image=&brand=&email=&category=

const PAGE_BG = "#FAFAF8";
const FONT = "'DM Sans', sans-serif";

function parseBgParam(raw: string | null): string {
    if (!raw) return PAGE_BG;
    const cleaned = raw.replace(/^#/, "").trim();
    if (/^[0-9a-fA-F]{3}$/.test(cleaned) || /^[0-9a-fA-F]{6}$/.test(cleaned)) {
        return `#${cleaned}`;
    }
    return PAGE_BG;
}

export default function ProposalPage() {
    const [searchParams] = useSearchParams();
    const { user } = useUser();
    const isLoggedIn = !!user;

    const bgColor = useMemo(() => parseBgParam(searchParams.get("bg")), [searchParams]);

    useEffect(() => {
        document.body.classList.add("pdp-audit-embed");
        return () => {
            document.body.classList.remove("pdp-audit-embed");
        };
    }, []);

    const product = useMemo<ProposalProductInput | undefined>(() => {
        const title = searchParams.get("title") || undefined;
        const url = searchParams.get("url") || undefined;
        const image = searchParams.get("image") || undefined;
        const brandName = searchParams.get("brand") || undefined;
        if (!title && !url) return undefined;
        return { title, url, image, brand_name: brandName };
    }, [searchParams]);

    const defaultCategoryId = useMemo(() => {
        const raw = Number(searchParams.get("category"));
        return Number.isInteger(raw) && raw > 0 ? raw : undefined;
    }, [searchParams]);

    // Audit report id from the PDP audit's "Create Proposal" CTA — links the
    // proposal back to the audit.
    const pdpAuditReportId = useMemo(() => {
        const raw = Number(searchParams.get("audit"));
        return Number.isInteger(raw) && raw > 0 ? raw : undefined;
    }, [searchParams]);

    // A catalog product's latest PDP score (Products page's Create Strategy).
    const pdpUrlScoreId = useMemo(() => {
        const raw = Number(searchParams.get("score"))
        return Number.isInteger(raw) && raw > 0 ? raw : undefined
    }, [searchParams]);

    // A Consumer Voice analysis (its report view's Create strategy CTA);
    // anonymous rows also carry their access token.
    const consumerVoiceAuditId = useMemo(() => {
        const raw = Number(searchParams.get("cv"));
        return Number.isInteger(raw) && raw > 0 ? raw : undefined;
    }, [searchParams]);
    // Or an AI Presence analysis (same CTA pattern).
    const aiPresenceAuditId = useMemo(() => {
        const raw = Number(searchParams.get("aip"));
        return Number.isInteger(raw) && raw > 0 ? raw : undefined;
    }, [searchParams]);
    // Anonymous Consumer Voice / AI Presence rows carry their access token.
    const intelAccessToken = searchParams.get("t") || undefined;

    const storeEmail =
        ((user as any)?.emailAddress as string | undefined) ||
        ((user as any)?.email_address as string | undefined) ||
        "";

    return (
        <div style={{ minHeight: "100vh", background: bgColor, fontFamily: FONT }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>
            <div style={{ width: 960, maxWidth: "100%", margin: "0 auto", padding: "48px 20px" }}>
                <ObjectiveProposalFlow
                    product={product}
                    defaultCategoryId={defaultCategoryId}
                    email={searchParams.get("email") || storeEmail}
                    requireCaptcha={!isLoggedIn}
                    pdpAuditReportId={pdpAuditReportId}
            pdpUrlScoreId={pdpUrlScoreId}
            consumerVoiceAuditId={consumerVoiceAuditId}
            aiPresenceAuditId={aiPresenceAuditId}
            intelAccessToken={intelAccessToken}
                />
                <ProposalDisclaimer />
            </div>
        </div>
    );
}
